import { selectLicenseProductState } from '../../../reducers/SmartDentReducer';
import React, { useState, useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { DEFAULT_LICENSE_PRODUCT } from '../../../actions/constants/LicenseProductConstant';
import { computeDealerRevokable, isValidId, resolveEffectiveCorpBudget, resolveSelfUsableCeiling, scopeOwnDealers } from '../../../lib/smartDentQuota';
import { buildParentOf } from '../../../lib/allocationContext';
import { extractDealerIdFromSk, formatCompanyInlineLabel, isSubsidiaryCompany } from '../../../lib/licenseHelper';
import { Translate as t } from '../../../config/Translate';
import { useLicenseProduct } from '../../../hooks/useLicenseProduct';
import { ORG_SUBSIDIARY } from '../../../config/appConstants';
// SEC-055 PR #546 리뷰: 사유 코드→문구 맵을 발급 모달과 공유하기 위해 lib 로 추출.
import {
    ALLOCATION_ERROR_MESSAGES as ERROR_MESSAGES,
    ALLOCATION_FALLBACK_MESSAGE,
    resolveLicenseErrorMessage,
} from '../../../lib/licenseErrorMessages';

// BUG-094 — '법인 하위에 법인과 딜러가 혼재' 라는 사용자 피드백 반영. dropdown 은 법인/
// 딜러 모두 후보가 되므로 모달 어휘를 'Allocation Target' 등 중립 표현으로 일반화.
// 내부 thunk 명(`AllocateToDealer`) 과 prop 명(`dealerId`) 은 호환성을 위해 유지.

// BUG-094 — dropdown 옵션의 type 배지 라벨. 법인 하위에 또 다른 법인 또는 딜러가
// 혼재할 수 있으므로 옵션마다 [법인]/[딜러] 표시로 사용자 시각 구분 보장.
//
// PR #187 리뷰 Suggestion #8 — unknown type 을 'Subsidiary' 로 잘못 라벨링하지 않도록
// 명시 신호가 있는 경우만 분류하고 그 외에는 null 반환 (옵션은 prefix 없이 렌더).
// 신규 type 값(예: 'agency', 'partner') 추가 시 사용자에게 잘못된 [법인] 라벨 노출 회피.
//
// 분류 신호:
//  - dealerType 이 'internal'/'external' 로 명시 → 'Dealer'
//  - org === 'Branch' 또는 isTypeChange === false → 'Subsidiary' (법인 측 명시 신호)
//  - 그 외 → null (unknown, prefix 미렌더)
export const getCompanyTypeBadge = (company) => {
    if (!company) return null;
    if (company.dealerType === 'internal' || company.dealerType === 'external') {
        return 'Dealer';
    }
    if (isSubsidiaryCompany(company)) {
        return ORG_SUBSIDIARY;
    }
    return null;
};

// AC-8: 낙관적 락 충돌 reason 식별.
const isConflictReason = (reason) =>
    reason === 'ALLOCATION_UPDATE_CONFLICT' || reason === 'REVOKE_UPDATE_CONFLICT';

const AllocateDealerModal = ({
    show,
    mode,
    packageId,
    corpId,
    existingRecord,
    onClose,
    onSuccess,
    corpAllocation,
    dealerAllocations,
    myReceivedAllocation,
    companies,
    allocationNodes,
    licenses,
    scopeContext,
    product: productProp,
}) => {
    const dispatch = useDispatch();
    const product = useLicenseProduct(productProp);
    const [dealerId, setDealerId] = useState('');
    const [tNew, setTNew] = useState(0);
    const [tRenew, setTRenew] = useState(0);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchedReceived, setFetchedReceived] = useState(null);

    const extractedDealerId = existingRecord?.sk ? extractDealerIdFromSk(existingRecord.sk) : null;
    const isReadOnlyDealer = mode === 'revoke' || existingRecord != null;

    // BUG-177: corpAllocation(자기 소유 corp 원장)이 없으면 우선 상위에게서 "받은" 레코드
    // (myReceivedAllocation, 중간 법인)를 예산으로 채택하고, 그것도 없으면 LicenseReducer.licenses
    // 의 기본 제품(DEFAULT_LICENSE_PRODUCT, SMARTDent) 레코드로 폴백한다. SSoT 헬퍼(resolveEffectiveCorpBudget) 로 통일 —
    // LicenseSubsidiaryTab / LicenseOverviewTab 과 동일 로직 공유.
    //
    // PR #528 R3 Major #1: isSubEntity 는 `dealers` useMemo 보다 먼저 계산돼야 한다 —
    // 아래 scopedDealers(:150)/corpIssuable(:156) 소비처가 이 값을 참조하므로 위치 유지.
    const { effectiveCorp, isSubEntity } = useMemo(() => {
        const lic = (licenses || []).find((l) => l._id === (product ?? DEFAULT_LICENSE_PRODUCT).licenseAppId);
        const licensesFallback = lic ? {
            t_initial: lic.t_initial, u_initial: lic.u_initial ?? 0,
            t_renewal: lic.t_renewal, u_renewal: lic.u_renewal ?? 0,
            i_initial: lic.i_initial ?? 0, i_renewal: lic.i_renewal ?? 0,
        } : null;
        return resolveEffectiveCorpBudget(corpAllocation, fetchedReceived ?? myReceivedAllocation, corpId, licensesFallback, scopeContext);
    }, [corpAllocation, fetchedReceived, myReceivedAllocation, corpId, licenses, scopeContext, product]);

    // 하위 회사 전체를 트리 순서(DFS)로 평탄화 — 깊이 정보 포함.
    // companies 배열은 백엔드가 자신을 제외하고 반환한다.
    const dealers = useMemo(() => {
        // BUG-198 v2: FEAT-050 S5b "법인도 후보 포함(BUG-170 봉인 해제)" 서브트리 전체 노출을
        // 반전 — 순차 할당 모델은 직속 자식만 후보로 노출한다(조부모→손자 건너뛰기 금지,
        // AllocateToDealer 가드와 동일 규칙).
        const base = (companies || []).filter(
            (c) => c.companyId && c.companyId !== corpId && c.parentCompanyId === corpId
        );
        // IMP-425 §12.1-A: 서버 가드(PR #535)가 자기할당을 tier 무관 fail-closed 차단하므로
        // self prepend 를 폐지 — 후보 목록은 직속 자식(base) 그대로 사용한다.
        const map = {};
        base.forEach((c) => { map[c.companyId] = { ...c, _children: [] }; });
        const roots = [];
        base.forEach((c) => {
            if (c.parentCompanyId === corpId || !map[c.parentCompanyId]) {
                roots.push(map[c.companyId]);
            } else {
                map[c.parentCompanyId]._children.push(map[c.companyId]);
            }
        });
        const result = [];
        const traverse = (node, depth) => {
            result.push({ companyId: node.companyId, name: node.name, depth });
            [...node._children]
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .forEach((child) => traverse(child, depth + 1));
        };
        [...roots]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .forEach((r) => traverse(r, 0));
        return result;
    }, [companies, corpId]);

    const scopedDealers = scopeOwnDealers(dealerAllocations, corpId, isSubEntity);

    // Allocate 모드: 로그인 법인이 줄 수 있는 가용 수량.
    // Revoke 모드: 선택된 대상에서 회수 가능한 수량 (over-issuance 모델 → t - u).
    //   i 가 아닌 u 기준 — voucher 발급(i)은 over-print 허용이고 회수는 활성화 budget 만 의미 있음.
    //
    // IMP-427 (사용자 확정 2026-07-31): Allocate 상한을 Allocation Summary 표시와 **동일 식**
    // (Self-Usable = t − 자기리딤 − Σ직속자식.t)으로 통일한다. 종전 최상위 법인 경로는
    // `issuable`(t − i − Σd)을 써 발급 누적 `i` 를 차감했고, 그 결과 화면 Summary 5/7 인데
    // 팝업 상한은 3/5 로 달랐다(사용자 보고). `i` 는 참고 값이며 가용량 계산에 쓰지 않는다
    // (마케팅 초과 발급 허용 모델 — `i > t` 정상). 발급 게이트는 BUG-200 에서 이미 리딤 기준으로
    // 이관됐고 할당 팝업 상한만 남아 있던 것을 여기서 정렬한다. 중간 법인은 종전대로 selfUsable.
    // → 종전 계약(IMP-156 `AllocateDealerModal.behavior.test.js`)을 대체한다.
    //
    // 원천은 표시와 같은 노드 모델을 우선 사용한다. `effectiveCorp` 가 corp 원장(2-seg)이면 그
    // `u` 는 자손 리딤까지 합친 롤업이라 그대로 selfUsable 에 넣으면 자식 리딤을 이중 차감한다
    // (BUG-191). 노드 미도착 시에는 IssueQr 게이트와 동일한 롤업 보정(u − Σ직속자식.u)을 적용한다.
    const parentOf = useMemo(() => buildParentOf(companies), [companies]);
    // PR #540 리뷰 Major: 종전 `nodeSelfUsable || resolveSelfUsableCeiling(...)` 는 노드가 도착한
    // 정상 경로에서 SSoT 결과를 버려, 노드 경로(strict 직속)와 SSoT 레코드 경로(당시 미필터 Σt)의
    // 모집단이 갈렸다 → 모달이 게이트보다 큰 값을 보여줄 수 있었다. 노드 우선 판단을 헬퍼 내부로
    // 흡수해 팝업·게이트가 같은 함수 한 곳을 지나게 한다.
    // allRows = 스코프 전 원본(mg 전체 3-seg). 노드가 비는 코너케이스(로딩 갭 / GetAllocationSubtree
    // 스코프 가드 리셋)에서 레코드 경로로 떨어질 때, 원장 롤업 보정을 게이트와 **같은 모집단**으로
    // 하기 위해 필요하다. 미전달 시 직속만 차감 → 자기소모 과대 → 팝업 천장이 게이트보다 작아진다.
    const corpIssuable = useMemo(
        () => resolveSelfUsableCeiling(effectiveCorp, scopedDealers, corpId, {
            nodesByCompanyId: allocationNodes,
            parentOf,
            allRows: dealerAllocations,
        }),
        [effectiveCorp, scopedDealers, corpId, allocationNodes, parentOf, dealerAllocations],
    );
    const dealerRevokable = computeDealerRevokable(existingRecord);
    const issuable = mode === 'revoke' ? dealerRevokable : corpIssuable;
    const availableLabel = mode === 'revoke' ? t('license.label.revokable') : t('license.label.available');

    useEffect(() => {
        if (show) {
            setDealerId(extractedDealerId || '');
            setTNew(0);
            setTRenew(0);
            setError(null);
            setInfo(null);
        }
    }, [show, extractedDealerId]);

    // BUG-179: 진입점 무관 self-fetch. 공유 슬롯(dealerAllocation)은 DealerTab 에서 선택된
    // 자식 레코드로 오염될 수 있으므로, 자기 받은 예산은 QueryMyReceivedAllocation 으로 직접
    // 조회해 로컬 state 에 담는다(공유 슬롯 미오염). 닫힐 때 초기화.
    useEffect(() => {
        if (!show || !corpId) { setFetchedReceived(null); return; }
        let alive = true;
        // BUG-211 R3(리뷰 Code Quality Major): fresh:true — 직전 Allocate/Revoke 직후 모달을
        // 다시 열었을 때 dedupe 캐시의 pre-write 상한이 보이지 않도록 한다.
        Promise.resolve(dispatch(SmartDentAction.QueryMyReceivedAllocation(corpId, { fresh: true, product })))
            .then((rec) => { if (alive) setFetchedReceived(rec || null); })
            .catch(() => { if (alive) setFetchedReceived(null); });
        return () => { alive = false; };
    }, [show, corpId, dispatch, product]);

    const handleConfirm = async () => {
        setError(null);
        setInfo(null);
        const effectiveDealerId = isReadOnlyDealer ? extractedDealerId : dealerId;

        if (!isValidId(effectiveDealerId || '')) {
            setError(t('license.message.error.invalidDealerId'));
            return;
        }
        if (tNew < 0 || tRenew < 0 || (tNew === 0 && tRenew === 0)) {
            setError(t('license.message.error.enterQtyGreaterThanZero'));
            return;
        }

        // PR #184 R1 FYI #2 — actor 는 thunk 가 getState 에서 강제 추출하므로
        // caller 가 전달하지 않는다 (위변조 방지, SuspendLicense 패턴 통일).
        const dispatchOnce = () => {
            const args = {
                packageId,
                corpId,
                dealerId: effectiveDealerId,
                tNew,
                tRenew,
                product,
            };
            return dispatch(
                mode === 'revoke'
                    ? SmartDentAction.RevokeFromDealer(args)
                    : SmartDentAction.AllocateToDealer(args),
            );
        };

        setLoading(true);
        try {
            let result = await dispatchOnce();

            // AC-8: 낙관적 락 충돌 시 1회 자동 재시도. 재시도 전 GetDealerAllocations
            // 로 fresh 상태를 다시 읽어 다음 시도의 ConditionExpression 비교값을 갱신.
            if (result?.ok === false && isConflictReason(result.reason)) {
                setInfo(t('common.message.concurrentChangeRetrying'));
                try {
                    // BUG-211: 표시 경로에 추가된 dedupe 캐시(최대 3초)를 타면 이 재조회가
                    // 방금 충돌난 것과 같은 stale 값을 그대로 돌려줄 수 있다 — fresh 로 우회
                    // (실제 write 재시도의 조건식 비교는 write thunk 자신이 항상 fresh 로 별도
                    // 읽으므로 이 dispatch 는 화면 표시값 갱신용이지만, 안내 문구도 최신값을
                    // 반영해야 하므로 동일하게 fresh 가 맞다).
                    await dispatch(SmartDentAction.GetDealerAllocations(packageId, corpId, { fresh: true, product }));
                } catch (e) {
                    console.warn('[AllocateDealerModal] re-fetch failed', e);
                }
                result = await dispatchOnce();
                setInfo(null);
            }

            if (result?.ok === false) {
                // SEC-055 PR #546 R2: 종전 인라인 폴백 `msg || result.reason` 이 매핑 없는 코드에서
                // **영문 사유 코드를 그대로 노출**했다(발급 모달과 같은 결함). 공유 resolver 로 통일.
                const issuableForMsg =
                    result.reason === 'INSUFFICIENT_DEALER_QUOTA' ? result.issuable : issuable;
                setError(resolveLicenseErrorMessage(
                    ERROR_MESSAGES, result.reason, issuableForMsg, t, t(ALLOCATION_FALLBACK_MESSAGE),
                ));
            } else {
                onSuccess?.();
            }
        } finally {
            setLoading(false);
        }
    };

    const readonlyCompanyLabel = useMemo(() => {
        if (!isReadOnlyDealer || !extractedDealerId) return extractedDealerId || '';
        const company = (companies || []).find((c) => c.companyId === extractedDealerId);
        // IMP-166: 인라인 라벨 포맷을 CompanyCell 과 공유(단일 SSoT). input value(string) 라 헬퍼 사용.
        return formatCompanyInlineLabel(extractedDealerId, company?.name || null);
    }, [isReadOnlyDealer, extractedDealerId, companies]);

    const renderDealerField = () => {
        if (isReadOnlyDealer) {
            return <input className="form-control" value={readonlyCompanyLabel} readOnly />;
        }
        if (dealers.length > 0) {
            return (
                <select
                    className="form-select"
                    value={dealerId}
                    onChange={(e) => setDealerId(e.target.value)}
                >
                    <option value="">{t('license.button.selectTarget')}</option>
                    {dealers.map((d) => {
                        const badge = getCompanyTypeBadge(d);
                        const prefix = badge ? `[${t(badge)}] ` : '';
                        return (
                        <option key={d.companyId} value={d.companyId}>
                            {'    '.repeat(d.depth)}
                            {d.depth > 0 ? '└ ' : ''}
                            {prefix}
                            {formatCompanyInlineLabel(d.companyId, d.name)}
                        </option>
                        );
                    })}
                </select>
            );
        }
        return (
            <input
                className="form-control"
                value={dealerId}
                onChange={(e) => setDealerId(e.target.value)}
                placeholder={t('license.placeholder.dealerId')}
            />
        );
    };

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header className="d-flex justify-content-between align-items-center">
                <Modal.Title className="mb-0">
                    {mode === 'revoke' ? t('license.button.revokeAllocation') : t('license.label.allocateQuota')}
                </Modal.Title>
                <button
                    type="button"
                    className="btn-close"
                    aria-label={t('common.button.close')}
                    onClick={onClose}
                />
            </Modal.Header>
            <Modal.Body className="p-3">
                {error && (
                    <div className="alert alert-danger py-2">{error}</div>
                )}
                {info && !error && (
                    <div className="alert alert-warning py-2">{info}</div>
                )}

                <div className="mb-3">
                    <label className="form-label">{t('license.label.allocationTarget')}</label>
                    {renderDealerField()}
                </div>

                <div className="mb-3">
                    <label className="form-label">
                        {t('license.label.initialQty')} ({availableLabel}: {issuable.newQty})
                    </label>
                    <input
                        type="number"
                        className="form-control"
                        value={tNew}
                        min={0}
                        max={issuable.newQty}
                        onChange={(e) => setTNew(parseInt(e.target.value, 10) || 0)}
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label">
                        {t('license.label.renewalQty')} ({availableLabel}: {issuable.renewQty})
                    </label>
                    <input
                        type="number"
                        className="form-control"
                        value={tRenew}
                        min={0}
                        max={issuable.renewQty}
                        onChange={(e) => setTRenew(parseInt(e.target.value, 10) || 0)}
                    />
                </div>

                <button
                    className="btn btn-primary w-100"
                    onClick={handleConfirm}
                    disabled={loading}
                >
                    {loading ? t('common.status.processing') : t('common.button.confirm')}
                </button>
            </Modal.Body>
        </Modal>
    );
};

const mapState = (state, ownProps) => {
    const licenseState = selectLicenseProductState(state, ownProps.product?.key);
    return {
        corpAllocation: licenseState.corpAllocation,
        dealerAllocations: licenseState.dealerAllocations,
        myReceivedAllocation: licenseState.myReceivedAllocation,
        companies: state.CompanyReducer.companies,
        licenses: state.LicenseReducer.licenses,
        scopeContext: licenseState.scopeContext,
        allocationNodes: licenseState.allocationNodes,
    };
};

export default connect(mapState)(AllocateDealerModal);
